import { useEffect, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger } from "@/components/ui/dialog";
import {
  CalendarBlank,
  Plus,
  Minus,
  Pencil,
  Trash,
  Eye,
  ShoppingCart,
  FilePdf,
  WhatsappLogo,
  CookingPot,
  MagnifyingGlass,
  CurrencyCircleDollar,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import { useLanguage } from "@/contexts/LanguageContext";
import { jsPDF } from "jspdf";
import EventPricing from "../components/EventPricing";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const formatCurrency = (value) =>
  new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(value);

export default function Events() {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const [events, setEvents] = useState([]);
  const [sheets, setSheets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [editingEvent, setEditingEvent] = useState(null);
  const [viewingEvent, setViewingEvent] = useState(null);
  const [eventSearch, setEventSearch] = useState("");
  const [pricingOpen, setPricingOpen] = useState(false);
  const [pricingEvent, setPricingEvent] = useState(null);
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    date: "",
    sheets: [],
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [eventsRes, sheetsRes] = await Promise.all([
        axios.get(`${API}/events`),
        axios.get(`${API}/technical-sheets`),
      ]);
      setEvents(eventsRes.data);
      setSheets(sheetsRes.data);
    } catch (error) {
      toast.error(t("ev_load_error"));
    } finally {
      setLoading(false);
    }
  };

  const toggleSheet = (sheetId) => {
    const sheet = sheets.find((s) => s.id === sheetId);
    if (!sheet) return;

    setFormData((prev) => {
      const exists = prev.sheets.find((s) => s.sheet_id === sheetId);
      if (exists) {
        return { ...prev, sheets: prev.sheets.filter((s) => s.sheet_id !== sheetId) };
      }
      return {
        ...prev,
        sheets: [...prev.sheets, { sheet_id: sheetId, sheet_name: sheet.name, quantity: 1 }],
      };
    });
  };

  const updateSheetQty = (sheetId, delta) => {
    setFormData((prev) => ({
      ...prev,
      sheets: prev.sheets.map((s) =>
        s.sheet_id === sheetId ? { ...s, quantity: Math.max(1, s.quantity + delta) } : s
      ),
    }));
  };

  const setSheetQtyDirect = (sheetId, value) => {
    const parsed = parseInt(value, 10);
    if (isNaN(parsed) || parsed < 1) return;
    setFormData((prev) => ({
      ...prev,
      sheets: prev.sheets.map((s) =>
        s.sheet_id === sheetId ? { ...s, quantity: parsed } : s
      ),
    }));
  };

  const isSheetSelected = (sheetId) => formData.sheets.some((s) => s.sheet_id === sheetId);
  const getSheetQty = (sheetId) => formData.sheets.find((s) => s.sheet_id === sheetId)?.quantity || 0;

  const getEstimatedCost = () => {
    return formData.sheets.reduce((total, sel) => {
      const sheet = sheets.find((s) => s.id === sel.sheet_id);
      return total + (sheet ? sheet.total_cost * sel.quantity : 0);
    }, 0);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (formData.sheets.length === 0) {
      toast.error(t("ev_need_sheet"));
      return;
    }

    try {
      const payload = {
        name: formData.name,
        description: formData.description || null,
        date: formData.date || null,
        sheets: formData.sheets,
      };

      if (editingEvent) {
        await axios.put(`${API}/events/${editingEvent.id}`, payload);
        toast.success(t("ev_updated"));
      } else {
        await axios.post(`${API}/events`, payload);
        toast.success(t("ev_created"));
      }

      setDialogOpen(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error(t("ev_save_error"));
    }
  };

  const handleEdit = (event) => {
    setEditingEvent(event);
    setFormData({
      name: event.name,
      description: event.description || "",
      date: event.date || "",
      sheets: event.sheets,
    });
    setDialogOpen(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm(t("ev_delete_confirm"))) return;
    try {
      await axios.delete(`${API}/events/${id}`);
      toast.success(t("ev_deleted"));
      fetchData();
    } catch (error) {
      toast.error(t("ev_delete_error"));
    }
  };

  const handleView = (event) => {
    setViewingEvent(event);
    setViewDialogOpen(true);
  };

  const handleGenerateShoppingList = (event) => {
    // Navigate to shopping list with pre-selected sheets from the event
    navigate("/shopping-list", { state: { fromEvent: event } });
  };

  const openPricing = (event) => {
    setPricingEvent(event);
    setPricingOpen(true);
  };

  const handleProduceEvent = async (event) => {
    if (!window.confirm(t("ev_produce_confirm").replace("{name}", event.name))) return;
    try {
      const res = await axios.post(`${API}/stock/produce-event/${event.id}`);
      if (res.data.success) {
        toast.success(res.data.message);
      } else {
        const items = res.data.insufficient.map(
          (i) => `${i.ingredient_name} (${i.sheet_name}): ${i.needed} ${i.unit} > ${i.available}`
        ).join("\n");
        toast.error(`${t("ev_insufficient")}:\n${items}`, { duration: 6000 });
      }
    } catch (error) {
      toast.error(t("ev_produce_error"));
    }
  };

  const generateEventPDF = (event) => {
    const doc = new jsPDF();

    doc.setFontSize(18);
    doc.setFont(undefined, "bold");
    doc.text("Evento", 20, 20);

    doc.setFontSize(14);
    doc.text(event.name, 20, 30);

    doc.setLineWidth(0.5);
    doc.line(20, 35, 190, 35);

    let yPos = 45;
    doc.setFontSize(10);
    doc.setFont(undefined, "normal");

    if (event.date) {
      doc.text(`Data: ${event.date}`, 20, yPos);
      yPos += 7;
    }
    if (event.description) {
      doc.text(`Descrição: ${event.description}`, 20, yPos);
      yPos += 7;
    }

    doc.text(`Custo Total Estimado: ${formatCurrency(event.estimated_cost)}`, 20, yPos);
    yPos += 12;

    doc.setFont(undefined, "bold");
    doc.text("FICHAS TÉCNICAS:", 20, yPos);
    yPos += 8;

    doc.setFillColor(240, 240, 240);
    doc.rect(20, yPos - 5, 170, 8, "F");
    doc.setFontSize(8);
    doc.text("Produto", 22, yPos);
    doc.text("Quantidade", 110, yPos);
    doc.text("Custo Unit.", 145, yPos);
    doc.text("Subtotal", 175, yPos, { align: "right" });

    yPos += 8;
    doc.setFont(undefined, "normal");

    event.sheets.forEach((item, index) => {
      const sheet = sheets.find((s) => s.id === item.sheet_id);
      const cost = sheet ? sheet.total_cost : 0;

      if (index % 2 === 0) {
        doc.setFillColor(250, 250, 250);
        doc.rect(20, yPos - 5, 170, 7, "F");
      }
      doc.text(item.sheet_name, 22, yPos);
      doc.text(`x${item.quantity}`, 110, yPos);
      doc.text(formatCurrency(cost), 145, yPos);
      doc.text(formatCurrency(cost * item.quantity), 175, yPos, { align: "right" });
      yPos += 7;
    });

    yPos += 3;
    doc.setLineWidth(0.5);
    doc.line(20, yPos, 190, yPos);
    yPos += 7;
    doc.setFont(undefined, "bold");
    doc.setFontSize(11);
    doc.text("TOTAL:", 130, yPos);
    doc.text(formatCurrency(event.estimated_cost), 175, yPos, { align: "right" });

    const pageCount = doc.internal.pages.length - 1;
    doc.setFontSize(8);
    doc.setFont(undefined, "normal");
    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i);
      doc.text(
        `Gerado por SOP & RAFT - ${new Date().toLocaleDateString("pt-BR")}`,
        105,
        290,
        { align: "center" }
      );
    }

    doc.save(`evento-${event.name.toLowerCase().replace(/\s+/g, "-")}.pdf`);
    toast.success(t("ev_pdf_done"));
  };

  const handleShareWhatsApp = (event) => {
    const sheetsText = event.sheets
      .map((s) => {
        const sheet = sheets.find((sh) => sh.id === s.sheet_id);
        const cost = sheet ? sheet.total_cost * s.quantity : 0;
        return `  • ${s.sheet_name} x${s.quantity} = ${formatCurrency(cost)}`;
      })
      .join("\n");

    const text =
      `📅 *Evento: ${event.name}*\n` +
      (event.date ? `📆 Data: ${event.date}\n` : "") +
      (event.description ? `📝 ${event.description}\n` : "") +
      `\n*Fichas Técnicas:*\n${sheetsText}\n\n` +
      `*Custo Total Estimado: ${formatCurrency(event.estimated_cost)}*\n\n` +
      `_Gerado por SOP & RAFT_`;

    window.open(`https://api.whatsapp.com/send?text=${encodeURIComponent(text)}`, "_blank");
    toast.success(t("ev_whatsapp_opening"));
  };

  const resetForm = () => {
    setFormData({ name: "", description: "", date: "", sheets: [] });
    setEditingEvent(null);
  };

  const handleDialogChange = (open) => {
    setDialogOpen(open);
    if (!open) resetForm();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="events-loading">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-zinc-900" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="events-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h2 className="text-xl md:text-2xl font-bold tracking-tight text-zinc-950" data-testid="events-title">
            {t("ev_title")}
          </h2>
          <p className="text-xs md:text-sm text-zinc-500 mt-1">
            {t("ev_subtitle")}
          </p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={handleDialogChange}>
          <DialogTrigger asChild>
            <Button data-testid="add-event-btn" className="bg-zinc-950 text-white hover:bg-zinc-800">
              <Plus size={18} className="mr-2" />
              {t("ev_new_event")}
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-[600px] max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="text-xl font-semibold tracking-tight">
                {editingEvent ? t("ev_edit_event") : t("ev_new_event")}
              </DialogTitle>
              <DialogDescription className="sr-only">{editingEvent ? t("ev_edit_event") : t("ev_new_event")}</DialogDescription>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-5 mt-4">
              <div>
                <Label className="text-xs font-semibold uppercase tracking-wider text-zinc-700">
                  {t("ev_name_label")}
                </Label>
                <Input
                  data-testid="event-name-input"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder={t("ev_name_placeholder")}
                  required
                  className="mt-1.5"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs font-semibold uppercase tracking-wider text-zinc-700">
                    {t("ev_date_opt")}
                  </Label>
                  <Input
                    data-testid="event-date-input"
                    type="date"
                    value={formData.date}
                    onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                    className="mt-1.5"
                  />
                </div>
                <div>
                  <Label className="text-xs font-semibold uppercase tracking-wider text-zinc-700">
                    {t("ev_estimated_cost")}
                  </Label>
                  <div className="mt-1.5 h-9 flex items-center px-3 bg-zinc-50 border border-zinc-200 rounded-md text-sm font-mono font-bold text-zinc-900">
                    {formatCurrency(getEstimatedCost())}
                  </div>
                </div>
              </div>

              <div>
                <Label className="text-xs font-semibold uppercase tracking-wider text-zinc-700">
                  {t("ev_desc_opt")}
                </Label>
                <textarea
                  data-testid="event-description-input"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder={t("ev_desc_placeholder")}
                  rows={2}
                  className="mt-1.5 w-full px-3 py-2 border border-zinc-200 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-zinc-950 focus:border-transparent resize-none"
                />
              </div>

              <div className="border-t border-zinc-200 pt-4">
                <Label className="text-xs font-semibold uppercase tracking-wider text-zinc-700 mb-3 block">
                  {t("ev_sheets")}
                </Label>

                {sheets.length === 0 ? (
                  <p className="text-sm text-zinc-500 text-center py-4">
                    {t("ev_no_sheets")}
                  </p>
                ) : (
                  <div className="space-y-2 max-h-60 overflow-y-auto">
                    {sheets.map((sheet) => {
                      const selected = isSheetSelected(sheet.id);
                      return (
                        <div
                          key={sheet.id}
                          data-testid={`event-sheet-${sheet.id}`}
                          className={`flex items-center justify-between p-2.5 rounded-lg border transition-colors ${
                            selected ? "border-zinc-950 bg-zinc-50" : "border-zinc-200 hover:border-zinc-300"
                          }`}
                        >
                          <div className="flex items-center gap-3">
                            <Checkbox
                              checked={selected}
                              onCheckedChange={() => toggleSheet(sheet.id)}
                              data-testid={`event-sheet-check-${sheet.id}`}
                            />
                            <div>
                              <p className="text-sm font-medium text-zinc-900">{sheet.name}</p>
                              <p className="text-xs text-zinc-500">
                                {t("ev_cost")}: {formatCurrency(sheet.total_cost)}
                              </p>
                            </div>
                          </div>
                          {selected && (
                            <div className="flex items-center gap-1.5">
                              <Button
                                type="button"
                                variant="outline"
                                size="icon"
                                className="h-7 w-7"
                                onClick={() => updateSheetQty(sheet.id, -1)}
                              >
                                <Minus size={14} />
                              </Button>
                              <Input
                                type="number"
                                min="1"
                                value={getSheetQty(sheet.id)}
                                onChange={(e) => setSheetQtyDirect(sheet.id, e.target.value)}
                                className="w-14 h-7 text-center text-sm"
                              />
                              <Button
                                type="button"
                                variant="outline"
                                size="icon"
                                className="h-7 w-7"
                                onClick={() => updateSheetQty(sheet.id, 1)}
                              >
                                <Plus size={14} />
                              </Button>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              <div className="flex justify-end space-x-3 pt-4 border-t border-zinc-200">
                <Button type="button" variant="outline" onClick={() => handleDialogChange(false)}>
                  {t("cancel")}
                </Button>
                <Button
                  type="submit"
                  data-testid="save-event-btn"
                  className="bg-zinc-950 text-white hover:bg-zinc-800"
                >
                  {editingEvent ? t("ev_update") : t("ev_save")}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Events List */}
      {events.length === 0 ? (
        <Card>
          <CardContent className="text-center py-12">
            <CalendarBlank size={48} className="mx-auto text-zinc-300 mb-3" />
            <p className="text-zinc-500" data-testid="no-events-message">
              {t("ev_no_events")}
            </p>
            <p className="text-sm text-zinc-400 mt-1">{t("ev_create_first")}</p>
          </CardContent>
        </Card>
      ) : (
        <>
          <div className="relative max-w-xs">
            <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
            <Input
              data-testid="event-search"
              value={eventSearch}
              onChange={(e) => setEventSearch(e.target.value)}
              placeholder={t("ev_search")}
              className="pl-9 h-9 text-sm"
            />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {events.filter(e => e.name.toLowerCase().includes(eventSearch.toLowerCase())).map((event) => (
            <Card key={event.id} className="border-zinc-200 shadow-sm" data-testid={`event-card-${event.id}`}>
              <CardHeader className="pb-3">
                <div className="flex items-start justify-between">
                  <div>
                    <CardTitle className="text-base font-semibold text-zinc-900">
                      {event.name}
                    </CardTitle>
                    {event.date && (
                      <p className="text-xs text-zinc-500 mt-1 flex items-center gap-1">
                        <CalendarBlank size={12} />
                        {new Date(event.date + "T12:00:00").toLocaleDateString("pt-BR")}
                      </p>
                    )}
                  </div>
                  <div className="bg-zinc-950 text-white rounded-md px-2.5 py-1">
                    <p className="text-xs font-bold font-mono">{formatCurrency(event.estimated_cost)}</p>
                  </div>
                </div>
                {event.description && (
                  <p className="text-xs text-zinc-500 mt-2">{event.description}</p>
                )}
              </CardHeader>
              <CardContent className="pt-0">
                <div className="space-y-1.5 mb-4">
                  {event.sheets.map((s) => (
                    <div
                      key={s.sheet_id}
                      className="flex justify-between text-xs text-zinc-600 bg-zinc-50 rounded px-2 py-1"
                    >
                      <span>{s.sheet_name}</span>
                      <span className="font-mono font-medium">x{s.quantity}</span>
                    </div>
                  ))}
                </div>

                <div className="flex flex-wrap gap-1.5">
                  <Button
                    variant="outline"
                    size="sm"
                    className="text-xs"
                    onClick={() => handleView(event)}
                    data-testid={`view-event-${event.id}`}
                  >
                    <Eye size={14} className="mr-1" />
                    {t("ev_view")}
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    className="text-xs"
                    onClick={() => handleGenerateShoppingList(event)}
                    data-testid={`event-shopping-${event.id}`}
                  >
                    <ShoppingCart size={14} className="mr-1" />
                    {t("ev_shopping")}
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    className="text-xs text-amber-700 border-amber-200 hover:bg-amber-50"
                    onClick={() => handleProduceEvent(event)}
                    data-testid={`event-produce-${event.id}`}
                  >
                    <CookingPot size={14} className="mr-1" />
                    {t("ev_produce")}
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    className="text-xs text-emerald-700 border-emerald-200 hover:bg-emerald-50"
                    onClick={() => openPricing(event)}
                    data-testid={`event-pricing-${event.id}`}
                  >
                    <CurrencyCircleDollar size={14} className="mr-1" />
                    {t("ev_pricing")}
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    className="text-xs"
                    onClick={() => handleEdit(event)}
                    data-testid={`edit-event-${event.id}`}
                  >
                    <Pencil size={14} className="mr-1" />
                    {t("ev_edit")}
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    className="text-xs text-rose-600 border-rose-200 hover:bg-rose-50"
                    onClick={() => handleDelete(event.id)}
                    data-testid={`delete-event-${event.id}`}
                  >
                    <Trash size={14} className="mr-1" />
                    {t("ev_delete")}
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
        </>
      )}

      {/* View Dialog */}
      <Dialog open={viewDialogOpen} onOpenChange={setViewDialogOpen}>
        <DialogContent className="sm:max-w-[600px]">
          <DialogHeader>
            <DialogTitle className="text-xl font-semibold tracking-tight">
              {t("ev_title")} - {viewingEvent?.name}
            </DialogTitle>
            <DialogDescription className="sr-only">{t("ev_title")}</DialogDescription>
          </DialogHeader>

          {viewingEvent && (
            <div className="space-y-5 mt-4">
              <div className="grid grid-cols-2 gap-4 p-4 bg-zinc-50 border border-zinc-200 rounded-lg">
                {viewingEvent.date && (
                  <div>
                    <p className="text-xs uppercase tracking-wider text-zinc-500 font-semibold">{t("date")}</p>
                    <p className="text-sm font-medium text-zinc-900 mt-1">
                      {new Date(viewingEvent.date + "T12:00:00").toLocaleDateString("pt-BR")}
                    </p>
                  </div>
                )}
                <div>
                  <p className="text-xs uppercase tracking-wider text-zinc-500 font-semibold">
                    {t("ev_estimated_cost")}
                  </p>
                  <p className="text-lg font-mono font-bold text-zinc-900 mt-1">
                    {formatCurrency(viewingEvent.estimated_cost)}
                  </p>
                </div>
                <div>
                  <p className="text-xs uppercase tracking-wider text-zinc-500 font-semibold">
                    {t("ev_sheets_label")}
                  </p>
                  <p className="text-sm font-medium text-zinc-900 mt-1">
                    {viewingEvent.sheets.length} {t("ev_sheets_count")}
                  </p>
                </div>
              </div>

              {viewingEvent.description && (
                <div className="p-3 bg-zinc-50 border border-zinc-200 rounded-lg">
                  <p className="text-xs uppercase tracking-wider text-zinc-500 font-semibold mb-1">
                    {t("description")}
                  </p>
                  <p className="text-sm text-zinc-700">{viewingEvent.description}</p>
                </div>
              )}

              <div>
                <p className="text-xs uppercase tracking-wider text-zinc-500 font-semibold mb-2">
                  {t("ev_sheets")}
                </p>
                <Table>
                  <TableHeader>
                    <TableRow className="bg-zinc-50">
                      <TableHead className="text-xs font-semibold">{t("ev_product")}</TableHead>
                      <TableHead className="text-xs font-semibold text-center">{t("ev_qty")}</TableHead>
                      <TableHead className="text-xs font-semibold text-right">{t("ev_unit_cost")}</TableHead>
                      <TableHead className="text-xs font-semibold text-right">{t("ev_subtotal")}</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {viewingEvent.sheets.map((s) => {
                      const sheet = sheets.find((sh) => sh.id === s.sheet_id);
                      const cost = sheet ? sheet.total_cost : 0;
                      return (
                        <TableRow key={s.sheet_id}>
                          <TableCell className="text-sm font-medium">{s.sheet_name}</TableCell>
                          <TableCell className="text-sm text-center font-mono">x{s.quantity}</TableCell>
                          <TableCell className="text-sm text-right font-mono">
                            {formatCurrency(cost)}
                          </TableCell>
                          <TableCell className="text-sm text-right font-mono font-bold">
                            {formatCurrency(cost * s.quantity)}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                    <TableRow className="bg-zinc-50 font-semibold">
                      <TableCell colSpan={3}>{t("total")}</TableCell>
                      <TableCell className="text-right font-mono">
                        {formatCurrency(viewingEvent.estimated_cost)}
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </div>

              <div className="flex justify-end space-x-2 pt-4 border-t border-zinc-200">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => generateEventPDF(viewingEvent)}
                  data-testid="event-view-pdf-btn"
                >
                  <FilePdf size={16} className="mr-1" />
                  PDF
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  className="text-green-700 border-green-200 hover:bg-green-50"
                  onClick={() => handleShareWhatsApp(viewingEvent)}
                  data-testid="event-view-whatsapp-btn"
                >
                  <WhatsappLogo size={16} className="mr-1" />
                  WhatsApp
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  className="text-emerald-700 border-emerald-200 hover:bg-emerald-50"
                  onClick={() => { setViewDialogOpen(false); openPricing(viewingEvent); }}
                  data-testid="event-view-pricing-btn"
                >
                  <CurrencyCircleDollar size={16} className="mr-1" />
                  {t("ev_pricing")}
                </Button>
                <Button
                  size="sm"
                  className="bg-zinc-950 text-white hover:bg-zinc-800"
                  onClick={() => {
                    setViewDialogOpen(false);
                    handleGenerateShoppingList(viewingEvent);
                  }}
                  data-testid="event-view-shopping-btn"
                >
                  <ShoppingCart size={16} className="mr-1" />
                  {t("ev_gen_shopping")}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Event Pricing Modal */}
      <EventPricing
        event={pricingEvent}
        open={pricingOpen}
        onOpenChange={setPricingOpen}
      />
    </div>
  );
}
